import { useEffect, useRef } from "react";

/**
 * Standard keyboard behavior for dialogs (the app-wide convention):
 *
 *   Enter  → activate the dialog's DEFAULT (primary) action
 *   Escape → cancel/close (MUI Dialog does this natively via `onClose`)
 *
 * Render this component anywhere INSIDE a MUI <Dialog>. It attaches a keydown
 * listener to that dialog's root, so with stacked dialogs only the one the
 * user is focused in (MUI traps focus in the topmost) reacts.
 *
 * Enter deliberately does NOT fire the default action when the user is:
 *   - on a button/link          → native activation must win, otherwise
 *                                 Tab→"Abbrechen"→Enter would CONFIRM
 *   - in a textarea             → Enter inserts a newline
 *   - in a combobox/autocomplete/listbox/menu → Enter selects an option
 *
 * Plain single-line text fields DO trigger the default action (form-submit
 * semantics), matching native <form> behavior.
 */
interface DialogDefaultActionProps {
  /** The dialog's primary action. Not called while `disabled` is true. */
  onAction: () => void;
  /** Mirror of the primary button's disabled state. */
  disabled?: boolean;
}

const INTERACTIVE_TARGETS =
  'button, a[href], textarea, [role="combobox"], [role="listbox"], [role="option"], [role="menu"], [role="menuitem"], .MuiAutocomplete-input, .MuiSelect-select';

export const DialogDefaultAction: React.FC<DialogDefaultActionProps> = ({
  onAction,
  disabled = false,
}) => {
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const stateRef = useRef({ onAction, disabled });
  stateRef.current = { onAction, disabled };

  useEffect(() => {
    const dialogRoot = anchorRef.current?.closest<HTMLElement>('[role="dialog"], .MuiDialog-paper');
    if (!dialogRoot) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest(INTERACTIVE_TARGETS)) return;
      if (target?.isContentEditable) return;
      if (stateRef.current.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      stateRef.current.onAction();
    };

    dialogRoot.addEventListener("keydown", onKeyDown);
    return () => dialogRoot.removeEventListener("keydown", onKeyDown);
  }, []);

  return <span ref={anchorRef} style={{ display: "none" }} />;
};

export default DialogDefaultAction;
